"use client";

import { type MouseEvent, useRef } from "react";

import { clusterHotspots } from "../components/MapboxMapView/utils/mapCalculations";
import type { HotspotRecord } from "../components/MapboxMapView/utils/mapMarkers";

/**
 * Configuration for dynamic cluster radius calculation
 */
export type ClusterRadiusConfig = {
  /** Zoom level at which clustering starts (default: 10) */
  minZoom?: number;
  /** Zoom level at which clustering stops (default: 16.5) */
  maxZoom?: number;
  /** Maximum cluster radius in meters at minZoom (default: 1500) */
  maxRadius?: number;
  /** Minimum cluster radius in meters before stopping (default: 20) */
  minRadius?: number;
};

/**
 * Default configuration for cluster radius calculation
 */
const DEFAULT_CLUSTER_CONFIG: Required<ClusterRadiusConfig> = {
  minZoom: 10,
  maxZoom: 18,
  maxRadius: 1500,
  minRadius: 20,
};

/**
 * Props for the useClusteredHotspots hook
 */
export type UseClusteredHotspotsProps = {
  /** Unique identifier for the clustered hotspots */
  sessionID: string;
  /** Array of hotspots to cluster */
  hotspots: HotspotRecord[];
  /** Current map zoom level */
  currentZoom: number;
  /** Optional callback when a cluster is clicked */
  onClusterClick?: (
    e: MouseEvent<HTMLElement>,
    lat: number,
    lng: number,
  ) => void;
  /** Label for the clustered hotspots */
  label?: string;
  /** Configuration for cluster radius calculation */
  clusterConfig?: ClusterRadiusConfig;
  /** When this value changes, the cached hotspots are cleared synchronously */
  resetKey?: unknown;
};

/**
 * Return type for the useClusteredHotspots hook
 */
export type UseClusteredHotspotsReturn = {
  /** Clustered hotspots ready for display */
  clusteredHotspots: HotspotRecord[];
  /** Clears all stored hotspots */
  clearHotspots: () => void;
};

/**
 * Calculates the cluster radius dynamically using exponential decay.
 * Lower zoom = larger clusters, higher zoom = smaller clusters.
 *
 * Formula: radius = maxRadius * e^(-k * (zoom - minZoom))
 * where k is calculated to ensure minRadius is reached at maxZoom.
 *
 * @param zoom - Current map zoom level
 * @param config - Cluster radius configuration
 * @returns Cluster radius in meters, or -1 for no clustering
 */
const getClusterRadiusForZoom = (
  zoom: number,
  config: Required<ClusterRadiusConfig>,
): number => {
  const { minZoom, maxZoom, maxRadius, minRadius } = config;

  // No clustering at high zoom levels
  if (zoom >= maxZoom) return -1;

  // Use max radius below min zoom
  if (zoom <= minZoom) return maxRadius;

  // Calculate decay constant k such that radius = minRadius when zoom = maxZoom
  // minRadius = maxRadius * e^(-k * (maxZoom - minZoom))
  // k = -ln(minRadius / maxRadius) / (maxZoom - minZoom)
  const k = -Math.log(minRadius / maxRadius) / (maxZoom - minZoom);

  // Calculate radius using exponential decay
  const radius = maxRadius * Math.exp(-k * (zoom - minZoom));

  return Math.round(radius);
};

/**
 * Hook for managing clustered hotspots on a map.
 * Handles merging, deduplication, and clustering of hotspots based on zoom level.
 *
 * @param props - Configuration options
 * @returns Clustered hotspots and a function to clear them
 *
 * @example
 * ```tsx
 * const { clusteredHotspots, clearHotspots } = useClusteredHotspots({
 *   hotspots: myHotspots,
 *   currentZoom: 15,
 *   onClusterClick: (e, lat, lng) => flyTo(lat, lng),
 * });
 * ```
 */
export const useClusteredHotspots = ({
  sessionID,
  hotspots,
  currentZoom,
  onClusterClick,
  label = "woningen",
  clusterConfig,
  resetKey,
}: UseClusteredHotspotsProps): UseClusteredHotspotsReturn => {
  /**
   * Stores all individual hotspots before clustering.
   * This preserves hotspots across viewport changes.
   */
  const originalHotspotsRef = useRef<HotspotRecord[]>([]);

  /** Tracks the previous resetKey to detect changes */
  const previousResetKeyRef = useRef<unknown>(resetKey);

  /** Merged configuration with defaults */
  const config: Required<ClusterRadiusConfig> = {
    ...DEFAULT_CLUSTER_CONFIG,
    ...clusterConfig,
  };

  /**
   * Clears all stored hotspots.
   * Useful when filters change and a fresh set of hotspots is needed.
   */
  const clearHotspots = () => {
    originalHotspotsRef.current = [];
  };

  const clusteredHotspots: HotspotRecord[] = (() => {
    // Clear cache synchronously when resetKey changes
    if (previousResetKeyRef.current !== resetKey) {
      originalHotspotsRef.current = [];
      previousResetKeyRef.current = resetKey;
    }

    if (!hotspots || !Array.isArray(hotspots)) {
      return [];
    }

    // Merge with existing hotspots (new hotspots first to preserve updated properties like active)
    let allIndividualHotspots = [...hotspots, ...originalHotspotsRef.current];

    // Deduplicate by id (keeps first occurrence = latest version from new hotspots)
    allIndividualHotspots = allIndividualHotspots.filter(
      (hotspot, index, self) =>
        index === self.findIndex((t) => t.id === hotspot.id),
    );

    // Save originals before clustering - preserves all individual hotspots
    originalHotspotsRef.current = allIndividualHotspots;

    // Apply clustering based on zoom level
    const clusterRadius = getClusterRadiusForZoom(currentZoom, config);

    const displayHotspots = clusterHotspots(
      allIndividualHotspots,
      clusterRadius,
      onClusterClick,
      label,
      sessionID,
    );

    return displayHotspots;
  })();

  return { clusteredHotspots, clearHotspots };
};
